import { useState, useEffect } from 'react'
import type { ComponentConfig, WizardState } from '../../../types/builder.types'
import { DataSourceStep } from './steps/DataSourceStep'
import { ComponentTypeStep } from './steps/ComponentTypeStep'
import { ConfigurationStep } from './steps/ConfigurationStep'
import { PreviewStep } from './steps/PreviewStep'
import { SaveStep } from './steps/SaveStep'
import { supabase } from '../../../lib/supabase'

const INITIAL_CONFIG: ComponentConfig = {
  componentType: 'kpi',
  dataSource: '',
}

interface ComponentWizardProps {
  editingComponentId?: string | null
  onComplete?: () => void
}

export function ComponentWizard({ editingComponentId, onComplete }: ComponentWizardProps) {
  const [state, setState] = useState<WizardState>({
    currentStep: 1,
    config: INITIAL_CONFIG,
  })
  const [loading, setLoading] = useState(false)
  const [editingComponentName, setEditingComponentName] = useState<string | null>(null)

  // Cargar datos del componente cuando estamos editando
  useEffect(() => {
    if (editingComponentId) {
      loadComponentData(editingComponentId)
    }
  }, [editingComponentId])

  const loadComponentData = async (componentId: string) => {
    try {
      setLoading(true)

      // @ts-ignore
      const { data, error } = await supabase
        .from('components')
        .select('id, name, type, data_source, configuration')
        .eq('id', componentId)
        .single()

      if (error) throw error

      if (data) {
        setEditingComponentName((data as any).name)
        setState({
          currentStep: 1,
          config: (data as any).configuration || INITIAL_CONFIG,
        })
      }
    } catch (err) {
      console.error('Error cargando componente:', err)
      alert('Error al cargar el componente para editar')
    } finally {
      setLoading(false)
    }
  }

  const handleConfigChange = (config: ComponentConfig) => {
    setState(prev => ({ ...prev, config }))
  }

  const handleNext = () => {
    setState(prev => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, 5)
    }))
  }

  const handlePrev = () => {
    setState(prev => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 1)
    }))
  }

  const stepProps = {
    config: state.config,
    onChange: handleConfigChange,
    onNext: handleNext,
    onPrev: handlePrev,
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando componente...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Progress */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {editingComponentId ? (
              <>✏️ Editando: {editingComponentName}</>
            ) : (
              <>Constructor Visual de Componentes</>
            )}
          </h1>

          {/* Progress Bar */}
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((stepNumber) => (
              <div key={stepNumber} className="flex items-center flex-1">
                {/* Step Circle */}
                <div className="flex items-center gap-2 flex-1">
                  <div
                    className={`
                      w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm
                      ${stepNumber < state.currentStep
                        ? 'bg-green-600 text-white'
                        : stepNumber === state.currentStep
                        ? 'bg-green-600 text-white ring-4 ring-green-100'
                        : 'bg-gray-200 text-gray-500'
                      }
                    `}
                  >
                    {stepNumber < state.currentStep ? '✓' : stepNumber}
                  </div>
                  <span
                    className={`
                      text-sm font-medium hidden md:block
                      ${stepNumber === state.currentStep ? 'text-green-700' : 'text-gray-500'}
                    `}
                  >
                    {getStepLabel(stepNumber)}
                  </span>
                </div>

                {/* Connector Line */}
                {stepNumber < 5 && (
                  <div
                    className={`
                      h-1 flex-1 mx-2
                      ${stepNumber < state.currentStep ? 'bg-green-600' : 'bg-gray-200'}
                    `}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {state.currentStep === 1 && <DataSourceStep {...stepProps} />}
        {state.currentStep === 2 && <ComponentTypeStep {...stepProps} />}
        {state.currentStep === 3 && <ConfigurationStep {...stepProps} />}
        {state.currentStep === 4 && <PreviewStep {...stepProps} />}
        {state.currentStep === 5 && (
          <SaveStep
            {...stepProps}
            editingComponentId={editingComponentId}
            onComplete={onComplete}
          />
        )}
      </div>
    </div>
  )
}

function getStepLabel(step: number): string {
  switch (step) {
    case 1: return 'Fuente de Datos'
    case 2: return 'Tipo de Componente'
    case 3: return 'Configuración'
    case 4: return 'Vista Previa'
    case 5: return 'Guardar'
    default: return ''
  }
}
